import type { Editor } from "@tiptap/react";
import type { SessionMessageWithParts } from "schemas/generated/opencode";
import type { ScrollToBottom } from "use-stick-to-bottom";
import type { MessageInputRef } from "./chat-elements/message-input/MessageInput";

import React, { useCallback, useLayoutEffect, useRef } from "react";

import { MessageInput } from "@agent-ui/features/chat/components/chat-elements/message-input/MessageInput";
import MessageLimitWrapper from "@agent-ui/features/chat/components/chat-elements/message-input/MessageLimitWrapper";
import { MessageList } from "@agent-ui/features/chat/components/chat-elements/message-list/MessageList";
import PlaceholderView from "@agent-ui/features/chat/components/chat-elements/placeholder/PlaceholderView";
import {
  Conversation,
  ConversationBottomSpacer,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
  PanelResizeScrollHandler,
} from "@agent-ui/features/chat/components/chat-elements/ui/Conversation";
import { ChatEditorContext } from "@agent-ui/features/chat/components/ReactEditorChat";
import { useLoadHistoricalMessages } from "@agent-ui/features/chat/hooks/useLoadHistoricalMessages";
import { useSessionActions } from "@agent-ui/features/chat/hooks/useOpencodeSession";
import { useSessionUuid } from "@agent-ui/features/chat/hooks/useSessionUuid";
import { useCurrentChatContext } from "@agent-ui/features/chat/providers/CurrentChatContext";
import {
  useIsSessionStreaming,
  useMessagesForSession,
} from "@agent-ui/features/chat/store/SessionStoreContext";
import Modifiers from "@agent-ui/features/modifiers/Modifiers";
import {
  useDisplayMode,
  useIsAnyElementSelected,
  useModifiersStoreActions,
} from "@agent-ui/features/modifiers/store/ModifiersStoreContext";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import twMerge from "@replo/design-system/utils/twMerge";
import { useReploHotkeys } from "@v2/hooks/useHotkeys";
import { useRequiredContext } from "replo-utils/react/context";
import { useStickToBottomContext } from "use-stick-to-bottom";

export const ChatMessageListContext = React.createContext<{
  messages: SessionMessageWithParts[];
}>({
  messages: [],
});

// NOTE (Ben, 2025-12-05): Captures scrollToBottom from StickToBottom's context into a ref.
// This allows us to call scrollToBottom from outside the StickToBottom tree (e.g., in handleSubmitWithScroll).
const ScrollToBottomCapture = ({
  scrollToBottomRef,
}: {
  scrollToBottomRef: React.MutableRefObject<ScrollToBottom | null>;
}) => {
  const { scrollToBottom } = useStickToBottomContext();

  useLayoutEffect(() => {
    scrollToBottomRef.current = scrollToBottom;
  }, [scrollToBottomRef, scrollToBottom]);

  return null;
};

const ChatGradient = ({
  side,
  className,
}: {
  side: "top" | "bottom";
  className?: string;
}) => (
  <div
    className={twMerge(
      "absolute inset-x-0 pointer-events-none",
      side === "top" ? "top-0" : "bottom-0",
      className,
    )}
  >
    <div
      className={twMerge(
        "h-3 w-full",
        side === "top"
          ? "bg-gradient-to-b from-white via-white/90 to-transparent"
          : "bg-gradient-to-t from-white via-white/90 to-transparent",
      )}
    />
  </div>
);

// NOTE (Ben, 2025-12-12): Custom infinite scroll component for inverse (scroll-to-top) loading.
// We can't use react-infinite-scroll-component's inverse mode because its isElementAtTop()
// function has a broken formula that never returns true when there's scrollable content.
const ChatInfiniteScroll = ({
  dataLength,
  next,
  hasMore,
  children,
}: React.PropsWithChildren<{
  dataLength: number;
  next: () => void;
  hasMore: boolean;
}>) => {
  const { scrollRef } = useStickToBottomContext();
  const [isLoading, setIsLoading] = React.useState(false);
  const lastDataLengthRef = React.useRef(dataLength);
  const scrollHeightBeforeLoadRef = React.useRef<number | null>(null);

  // NOTE (Ben, 2025-12-12): Preserve scroll position when new data is prepended
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    if (
      dataLength !== lastDataLengthRef.current &&
      scrollHeightBeforeLoadRef.current !== null
    ) {
      // NOTE (Ben, 2025-12-12): Calculate how much content was added and adjust scroll position
      const scrollHeightDiff =
        el.scrollHeight - scrollHeightBeforeLoadRef.current;
      el.scrollTop = el.scrollTop + scrollHeightDiff;

      setIsLoading(false);
      scrollHeightBeforeLoadRef.current = null;
      lastDataLengthRef.current = dataLength;
    }
  }, [dataLength, scrollRef]);

  // NOTE (Ben, 2025-12-18): Auto-load more if content doesn't fill the container
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    if (el.scrollHeight <= el.clientHeight && hasMore && !isLoading) {
      scrollHeightBeforeLoadRef.current = el.scrollHeight;
      setIsLoading(true);
      next();
    }
  }, [scrollRef, hasMore, isLoading, next]);

  // NOTE (Ben, 2025-12-12): Custom scroll detection for loading more at the top
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const handleScroll = () => {
      const isNearTop = el.scrollTop < 100;

      if (isNearTop && hasMore && !isLoading) {
        // NOTE (Ben, 2025-12-12): Save scroll height before loading so we can restore position after
        scrollHeightBeforeLoadRef.current = el.scrollHeight;
        setIsLoading(true);
        next();
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [scrollRef, hasMore, isLoading, next]);

  return (
    <>
      {isLoading && hasMore && (
        <div className="flex items-center justify-center py-2">
          <Spinner />
        </div>
      )}
      {children}
    </>
  );
};

const ModifiersMenu = () => {
  const isAnyElementSelected = useIsAnyElementSelected();

  return (
    <div className="absolute bottom-2 left-0 right-0 top-2 flex items-end pointer-events-none">
      <div className="flex flex-col pointer-events-auto max-h-full max-w-full w-full">
        {isAnyElementSelected && <Modifiers />}
      </div>
    </div>
  );
};

const MessageInputForm = ({
  editor,
  handleSubmitWithScrollToBottom,
  messageInputRef,
  isChatLoading,
}: {
  editor: Editor | null;
  handleSubmitWithScrollToBottom: (opts: {
    chatEditor: Editor;
    event: React.FormEvent | null;
  }) => void;
  messageInputRef: React.RefObject<MessageInputRef>;
  isChatLoading: boolean;
}) => {
  const { files, setFiles } = useCurrentChatContext();
  const displayMode = useDisplayMode();
  const modifierActions = useModifiersStoreActions();
  const { abortSession } = useSessionActions();
  const sessionUuid = useSessionUuid();

  const handleStop = useCallback(() => {
    if (sessionUuid) {
      void abortSession({ sessionUuid });
    }
  }, [sessionUuid, abortSession]);

  return (
    <div className="relative min-w-0">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!editor) {
            return;
          }
          handleSubmitWithScrollToBottom({
            chatEditor: editor,
            event: e,
          });
        }}
      >
        <div className="relative">
          <MessageLimitWrapper>
            <MessageInput
              ref={messageInputRef}
              files={files}
              setFiles={setFiles}
              allowAttachments={true}
              enableInterrupt={true}
              editor={editor}
              isChatLoading={isChatLoading}
              onStop={handleStop}
              editorClassName="max-h-[120px]"
              displayMode={displayMode}
              toggleDisplayMode={modifierActions.toggleDisplayMode}
            />
          </MessageLimitWrapper>
        </div>
      </form>
    </div>
  );
};

interface ChatProps {
  handleSubmit: (opts: {
    chatEditor: Editor;
    event: React.FormEvent | null;
  }) => void;
  // NOTE (Ben, 2025-12-05): Ref populated with handleSubmitWithScroll so parent components
  // can trigger submit with scroll behavior. Needed because Enter key submissions go through
  // ChatEditorProvider.onSubmit which bypasses this component's form onSubmit handler.
  onSubmitRef?: React.MutableRefObject<
    | ((opts: { chatEditor: Editor; event: React.FormEvent | null }) => void)
    | null
  >;
}

export function Chat({ handleSubmit, onSubmitRef }: ChatProps) {
  const { editor } = useRequiredContext(ChatEditorContext);
  const { hasNextPage, fetchNextPage } = useLoadHistoricalMessages();
  const messageInputRef = useRef<MessageInputRef>(null);
  const scrollToBottomRef = useRef<ScrollToBottom | null>(null);

  const sessionUuid = useSessionUuid();
  const opencodeMessages = useMessagesForSession(sessionUuid);
  const isChatLoading = useIsSessionStreaming(sessionUuid);

  // NOTE (Ben, 2025-11-04): If the chat has no owner (i.e. no chat created yet), or the owner is the current user, or the current user is a superuser, then they can edit the chat.
  // Otherwise, we hide the chat input.
  // TODO (Max, 2026-02-17, REPL-23979): Implement canEdit logic
  // const canEdit = !ownerId || ownerId === user?.id || user?.isSuperuser;
  const canEdit = true;

  const displayMode = useDisplayMode();
  const showModifiers = canEdit && displayMode === "select";
  const hasMessages = opencodeMessages.length > 0;

  // NOTE (Ben, 2025-12-05): Wrapper that triggers scroll to bottom after submitting a message.
  const handleSubmitWithScrollToBottom = useCallback(
    (opts: { chatEditor: Editor; event: React.FormEvent | null }) => {
      // Stop voice recording if active
      messageInputRef.current?.stopVoiceRecording();
      handleSubmit(opts);
      requestAnimationFrame(() => {
        void scrollToBottomRef.current?.({ animation: "smooth" });
      });
    },
    [handleSubmit],
  );

  // NOTE (Ben, 2025-12-05): Expose the scroll-enabled submit handler to parent components
  useLayoutEffect(() => {
    if (onSubmitRef) {
      onSubmitRef.current = handleSubmitWithScrollToBottom;
    }
  }, [onSubmitRef, handleSubmitWithScrollToBottom]);

  useReploHotkeys({
    toggleVoice: () => {
      messageInputRef.current?.toggleVoiceRecording();
    },
  });

  return (
    <ChatMessageListContext.Provider value={{ messages: opencodeMessages }}>
      <div className="grid h-full w-full max-w-[calc(min(880px,100%))] mx-auto grid-rows-[1fr_auto] overflow-hidden min-h-0">
        <div className="relative flex min-h-0 h-full w-full">
          <Conversation>
            <ScrollToBottomCapture scrollToBottomRef={scrollToBottomRef} />
            <PanelResizeScrollHandler />
            <ConversationContent>
              {!hasMessages && !isChatLoading && (
                <ConversationEmptyState className="min-h-full">
                  <PlaceholderView
                    editor={editor}
                    messageInputRef={messageInputRef}
                  />
                </ConversationEmptyState>
              )}

              <ChatInfiniteScroll
                dataLength={opencodeMessages.length}
                next={() => void fetchNextPage()}
                hasMore={hasNextPage}
              >
                <MessageList />
              </ChatInfiniteScroll>
              {hasMessages && <ConversationBottomSpacer />}
            </ConversationContent>

            <ConversationScrollButton />

            <ChatGradient side="top" />
            <ChatGradient side="bottom" />
          </Conversation>

          {showModifiers && <ModifiersMenu />}
        </div>

        {canEdit && (
          <MessageInputForm
            editor={editor}
            handleSubmitWithScrollToBottom={handleSubmitWithScrollToBottom}
            messageInputRef={messageInputRef}
            isChatLoading={isChatLoading}
          />
        )}
      </div>
    </ChatMessageListContext.Provider>
  );
}
